import React, { useState, useEffect } from 'react'
import ContactSearch from './_components/ContactSearch'
import FriendList from './_components/friends/FriendList'
import GroupList from './_components/group/GroupList'
import FriendDetail from './_components/friends/FriendDetail'
import GroupDetail from './_components/group/GroupDetail'
import { useFriendStore } from '@/stores/useFriendStore'
import { useGroupStore } from '@/stores/useGroupStore'
import { FriendInfo, GroupInfo, GroupListItem } from '@/api/types'
import { useChatActions } from '@/hooks/useChatActions'
import MessageManager from '@/utils/ably-im/message-manager'
import { ContactPageTab, usePageDataStore } from '@/stores/usePageDataStore'
import { logPrint } from '@/utils/my-utils'



interface ContactsModuleProps {
  onNavigateToUserChat?: (userId: string) => Promise<void>
  onNavigateToGroupChat?: (conversationId: string) => Promise<void>
}

export default function ContactsModule({
  onNavigateToUserChat,
  onNavigateToGroupChat
}: ContactsModuleProps): React.JSX.Element {
  const [searchQuery, setSearchQuery] = useState('')
  const {
    groupActiveTab,
    setGroupActiveTab,
    groupSelectedFriend,
    groupSelectedGroup,
    setGroupSelectedFriend,
    setGroupSelectedGroup
  } = usePageDataStore()


  logPrint("联系人页面====", groupSelectedGroup)

  // 获取 stores 数据
  const {
    friendList,
    friendListLoading,
    friendListError,
  } = useFriendStore()

  const {
    groupListLoading,
    groupListError,
    getGroupList
  } = useGroupStore()

  const {
    getConversations
  } = useChatActions()

  // 初始化数据
  useEffect(() => {
  }, [])

  // 清除选中状态当切换标签时（保留在群组标签下的已选群组）
  useEffect(() => {
    if (groupActiveTab === 'friends') {
      // 切到好友标签时清除已选群组
      setGroupSelectedGroup(null)
    } else if (groupActiveTab === 'groups') {
      // 切到群组标签时清除已选好友，但不清除已选群组
      setGroupSelectedFriend(null)
    }
  }, [groupActiveTab])

  // 过滤好友数据
  const filteredFriends = friendList.filter((friend) => {
    const displayName = friend.alias || friend.nickname
    return displayName.toLowerCase().includes(searchQuery.toLowerCase())
      || friend.handle_rand.toString().includes(searchQuery.toLowerCase())
  })

  // 按首字母分组好友
  const friendsList = filteredFriends.reduce(
    (groups, friend) => {
      const displayName = friend.alias || friend.nickname
      const firstLetter = displayName[0]?.toUpperCase() || '#'
      if (!groups[firstLetter]) {
        groups[firstLetter] = []
      }
      groups[firstLetter].push(friend)
      return groups
    },
    {} as Record<string, FriendInfo[]>
  )

  const handleFriendSelect = (friend: FriendInfo): void => {
    setGroupSelectedFriend(friend)
    setGroupSelectedGroup(null)
  }

  const handleGroupSelect = (group: GroupListItem): void => {
    setGroupSelectedGroup(group)
    setGroupSelectedFriend(null)
  }

  // 处理群组移除后的回调
  const handleGroupRemoved = (): void => {
    setGroupSelectedGroup(null)
    getConversations();
    getConversations();
  }

  // 处理好友移除后的回调
  const handleFriendRemoved = (): void => {
    setGroupSelectedFriend(null)
    getConversations();
  }

  // 处理群组创建成功后的回调
  const handleCreateGroupSuccess = async (group: GroupInfo): Promise<void> => {
    const groupMap = await getGroupList();
    if (group.conversation_id) {
      setGroupActiveTab(ContactPageTab.GROUPS)
      setGroupSelectedGroup(groupMap.get(group.conversation_id) || null)
    }
    await getConversations();
    // 跳转到群组聊天
    setTimeout(() => {
      onNavigateToGroupChat?.(group.conversation_id);
    }, 500);
  }

  return (
    <div className="h-full bg-white flex">
      {/* 左侧联系人列表区域 */}
      <div className="w-80 flex flex-col border-r border-gray-200">
        {/* 搜索栏和标签页 */}
        <div className="p-4 border-b border-gray-100">
          <ContactSearch
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onTabChange={setGroupActiveTab}
            onCreateGroupSuccess={handleCreateGroupSuccess}
          />
        </div>

        {/* 联系人列表 */}
        {groupActiveTab === ContactPageTab.FRIENDS ? (
          <div key="friends-container" className="flex-1 overflow-auto">
            <FriendList
              friendsList={friendsList}
              onFriendSelect={handleFriendSelect}
              selectedFriend={groupSelectedFriend}
              isLoading={friendListLoading}
              error={friendListError}
            />
          </div>
        ) : (
          <div key="groups-container" className="flex-1 overflow-auto">
            <GroupList
              onGroupSelect={handleGroupSelect}
              selectedGroup={groupSelectedGroup}
              isLoading={groupListLoading}
              error={groupListError}
            />
          </div>
        )}

      </div>

      {/* 右侧详情面板 */}
      <div className="flex-1">
        {groupSelectedFriend ? (
          <FriendDetail
            friend={groupSelectedFriend}
            onNavigateToUserChat={onNavigateToUserChat}
            onFriendRemoved={handleFriendRemoved}
          />
        ) : groupSelectedGroup ? (
          <GroupDetail
            group={groupSelectedGroup}
            onNavigateToGroupChat={onNavigateToGroupChat}
            onGroupRemoved={handleGroupRemoved}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-gray-500">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="mt-2">
                {groupActiveTab === ContactPageTab.FRIENDS ? '暂无好友，请搜索添加好友' : '暂无群组，请创建或加入群组'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
